import type { WallMicState, WallNocMicMode } from "@/types/ai-assistant-voice";

/**
 * Mesin keadaan mikrofon Mode NOC di /wall (murni, tanpa DOM): kapan jalur
 * mikrofon dibuka atau ditutup.
 * - `ptt`: terbuka hanya selama tombol ditahan.
 * - `gate`: terbuka kecuali saat Jenna terdengar + `settleMs` hening sesudahnya.
 * - `ptt_gate`: harus ditahan DAN Jenna tidak sedang terdengar.
 * Menahan tombol saat Jenna berbicara = interupsi sengaja: mic dibuka paksa
 * selama tombol ditahan dan `onBargeIn(true)` memotong suara Jenna.
 */

export interface MicGateTimers {
	set(fn: () => void, ms: number): unknown;
	clear(handle: unknown): void;
}

export interface MicGateOptions {
	mode: WallNocMicMode;
	/** Ekor hening (ms) sebelum mic dibuka lagi setelah suara Jenna berhenti. */
	settleMs: number;
	/** Buka/tutup jalur mikrofon; dipanggil hanya saat nilainya berubah. */
	setOpen(open: boolean): void;
	/** Tombol ditahan saat Jenna berbicara (true), dan saat tombol dilepas (false). */
	onBargeIn(active: boolean): void;
	/** Lencana berubah. */
	onState?(state: WallMicState): void;
	timers?: MicGateTimers;
}

export interface MicGate {
	/** Tombol bicara ditekan; diabaikan di mode `gate` atau bila sudah ditahan. */
	press(): void;
	/** Tombol dilepas; aman dipanggil berulang (mencegah tombol macet). */
	release(): void;
	/** Dipanggil tiap sampel audio Jenna yang terdengar. */
	audible(): void;
	state(): WallMicState;
	/** Hentikan timer; panggilan berikutnya diabaikan. */
	stop(): void;
}

const REAL_TIMERS: MicGateTimers = {
	set: (fn, ms) => setTimeout(fn, ms),
	clear: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

/** Buat gerbang mikrofon; langsung menerapkan keadaan awal lewat `setOpen`. */
export function createMicGate(opts: MicGateOptions): MicGate {
	const { mode, settleMs, timers = REAL_TIMERS } = opts;
	const usesPtt = mode !== "gate";
	const autoMute = mode !== "ptt";
	let held = false;
	let bargedIn = false;
	let aiSpeaking = false;
	let settleTimer: unknown = null;
	let stopped = false;
	let open: boolean | null = null;
	let shown: WallMicState | null = null;

	const isOpen = () =>
		(!usesPtt || held) && (!autoMute || !aiSpeaking || bargedIn);

	const current = (): WallMicState =>
		isOpen() ? "open" : aiSpeaking ? "ai_speaking" : "closed";

	const apply = () => {
		const next = isOpen();
		if (next !== open) {
			open = next;
			opts.setOpen(next);
		}
		const state = current();
		if (state !== shown) {
			shown = state;
			opts.onState?.(state);
		}
	};

	apply();

	return {
		press() {
			if (stopped || !usesPtt || held) return;
			held = true;
			if (aiSpeaking) {
				bargedIn = true;
				opts.onBargeIn(true);
			}
			apply();
		},
		release() {
			if (stopped || !held) return;
			held = false;
			if (bargedIn) {
				bargedIn = false;
				opts.onBargeIn(false);
			}
			apply();
		},
		audible() {
			if (stopped) return;
			if (settleTimer !== null) timers.clear(settleTimer);
			aiSpeaking = true;
			settleTimer = timers.set(() => {
				settleTimer = null;
				aiSpeaking = false;
				apply();
			}, settleMs);
			apply();
		},
		state: current,
		stop() {
			if (stopped) return;
			stopped = true;
			if (settleTimer !== null) timers.clear(settleTimer);
			settleTimer = null;
		},
	};
}
